import React, {useCallback, useMemo, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {Colors} from '../../../constants/colors'
import {ComplianceDecisionModel, ComplianceStatus,} from '../../../redux/companyMonitorTypes'
import {ColumnDef, flexRender, getCoreRowModel, useReactTable,} from '@tanstack/react-table'
import Badge from 'react-bootstrap/Badge'
import {formatDateTimeLocale} from '../../../utils/dateHelper'
import MDEditor from '@uiw/react-md-editor'
import '@uiw/react-markdown-preview/markdown.css'
import Modal from '../../Modal'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEye} from '@fortawesome/free-solid-svg-icons'

interface Props {
  items: ComplianceDecisionModel[]
  bg?: string
  hoverBg?: string
}

interface StyleProps {
  bg: string
  hoverBg: string
}

type MarkdownDetails = {
  title: string
  markdown: string
}

const removeMarkdownHeadingAnchor = (node: any, _index?: number, parent?: any) => {
  if (node.tagName === 'a' && parent && /^h(1|2|3|4|5|6)/.test(parent.tagName)) {
    parent.children = parent.children.slice(1)
  }
}

const UserComplianceDecisionList = ({
                                      items,
                                      bg = Colors.darkGrayCard,
                                      hoverBg = Colors.darkGrayHover,
                                    }: Props) => {
  const styles = useStyles({bg, hoverBg})
  const [markdownDetails, setMarkdownDetails] = useState<MarkdownDetails | null>(null)

  const getComplianceBadge = (status: ComplianceStatus) => {
    switch (status) {
      case 'compliant':
        return (
          <Badge text="dark" bg="success">
            Compliant
          </Badge>
        )
      case 'nonCompliant':
        return (
          <Badge text="dark" bg="danger">
            Non-compliant
          </Badge>
        )
      case 'unknown':
      default:
        return (
          <Badge text="dark" bg="warning">
            Decision required
          </Badge>
        )
    }
  }

  const handleCloseMarkdownDetails = () => {
    setMarkdownDetails(null)
  }

  const openMarkdownDetails = useCallback((title: string, markdown: string) => {
    setMarkdownDetails({title, markdown})
  }, [])

  const renderMarkdown = useCallback((title: string, value: string | null | undefined) => {
    const markdown = value?.trim()

    if (!markdown) {
      return <span>-</span>
    }

    return (
      <div
        className={styles.markdownCell}
        role="button"
        tabIndex={0}
        onClick={(event) => {
          if ((event.target as HTMLElement).closest('a')) {
            return
          }

          openMarkdownDetails(title, markdown)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openMarkdownDetails(title, markdown)
          }
        }}
        aria-label={`View full ${title.toLowerCase()}`}
      >
        <div data-color-mode="dark" className={styles.markdownPreview}>
          <MDEditor.Markdown
            className={styles.markdownContent}
            source={markdown}
            rehypeRewrite={removeMarkdownHeadingAnchor}
          />
        </div>
        <button
          type="button"
          className={styles.viewMarkdownButton}
          onClick={(event) => {
            event.stopPropagation()
            openMarkdownDetails(title, markdown)
          }}
        >
          <FontAwesomeIcon icon={faEye} className={styles.viewMarkdownIcon}/>
          View full
        </button>
      </div>
    )
  }, [
    styles.markdownCell,
    styles.markdownContent,
    styles.markdownPreview,
    styles.viewMarkdownButton,
    styles.viewMarkdownIcon,
    openMarkdownDetails,
  ])

  const columns = useMemo<ColumnDef<ComplianceDecisionModel>[]>(
    () => [
      {
        id: 'status',
        header: 'Decision',
        cell: ({row}) => (
          <div style={{display: 'flex', alignItems: 'center'}}>
            {getComplianceBadge(row.original.status)}
          </div>
        ),
      },
      {
        id: 'decidedAt',
        header: 'Decided at',
        cell: ({row}) => (
          <span>{formatDateTimeLocale(row.original.decidedAt)}</span>
        ),
      },
      {
        id: 'decidedBy',
        header: 'Decided by',
        cell: ({row}) => <span>{row.original.decidedBy}</span>,
      },
      {
        id: 'reason',
        header: 'Reason',
        cell: ({row}) => renderMarkdown('Reason', row.original.reason),
      },
      {
        id: 'investigationReference',
        header: 'Investigation reference',
        cell: ({row}) => renderMarkdown('Investigation reference', row.original.investigationReference),
      },
    ],
    [renderMarkdown]
  )

  const table = useReactTable({
    data: items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={styles.headerRow}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className={styles.headerCell}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={styles.bodyRow}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={styles.bodyCell}>
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </td>
              ))}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <Modal
        title={markdownDetails?.title ?? ''}
        show={markdownDetails !== null}
        okButtonTitle='Close'
        hideCancelButton={true}
        onHide={handleCloseMarkdownDetails}
        onOkClick={handleCloseMarkdownDetails}
        onCancelClick={handleCloseMarkdownDetails}
        size='lg'
      >
        <div data-color-mode="dark">
          <MDEditor.Markdown
            className={styles.markdownDetailsContent}
            source={markdownDetails?.markdown ?? ''}
            rehypeRewrite={removeMarkdownHeadingAnchor}
          />
        </div>
      </Modal>
    </div>
  )
}

export default UserComplianceDecisionList

const useStyles = createUseStyles<string, StyleProps>({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerRow: {
    backgroundColor: 'transparent',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    backgroundColor: ({bg}) => bg,
    borderRadius: '4px',
    fontWeight: 'bold',
    color: Colors.darkGray2,
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    }
  },
  bodyRow: {
    backgroundColor: ({bg}) => bg,
    '&:hover': {
      backgroundColor: ({hoverBg}) => hoverBg,
    },
    '&:hover $markdownPreview::after': {
      background: ({hoverBg}) => `linear-gradient(to bottom, transparent, ${hoverBg})`,
    },
  },
  bodyCell: {
    padding: '6px 12px',
    textAlign: 'left',
    verticalAlign: 'top',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  markdownCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
    minWidth: 220,
    padding: '6px 8px',
    border: '1px solid transparent',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover, &:focus-visible': {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      borderColor: Colors.euro_600,
      outline: 'none',
    },
  },
  markdownPreview: {
    position: 'relative',
    width: '100%',
    maxHeight: 192,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 24,
      background: ({bg}) => `linear-gradient(to bottom, transparent, ${bg})`,
      pointerEvents: 'none',
    },
  },
  viewMarkdownButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: 0,
    border: 0,
    backgroundColor: 'transparent',
    color: Colors.euro_300,
    fontFamily: '"Aestetico-Bold", sans-serif',
    fontSize: 12,
    lineHeight: '18px',
    cursor: 'pointer',
    transition: '0.2s',
    '&:hover': {
      color: Colors.euro_100,
      textDecoration: 'underline',
    },
  },
  viewMarkdownIcon: {
    fontSize: 11,
  },
  markdownContent: {
    backgroundColor: 'transparent !important',
    color: Colors.darkGray2,
    fontFamily: '"Aestetico-Regular", sans-serif',
    fontSize: '14px',
    lineHeight: '1.6rem',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    '& p': {
      margin: 0,
    },
    '& p:not(:last-child)': {
      marginBottom: 6,
    },
    '& ul, & ol': {
      marginTop: 0,
      marginBottom: 0,
      paddingLeft: 18,
    },
    '& pre': {
      margin: 0,
      whiteSpace: 'pre-wrap',
    },
  },
  markdownDetailsContent: {
    backgroundColor: 'transparent !important',
    color: Colors.darkGray2,
    fontFamily: '"Aestetico-Regular", sans-serif',
    fontSize: '14px',
    lineHeight: '1.6rem',
    wordBreak: 'break-word',
    '& p': {
      marginTop: 0,
      marginBottom: 10,
    },
    '& ul, & ol': {
      marginTop: 0,
      marginBottom: 10,
      paddingLeft: 22,
    },
    '& pre': {
      whiteSpace: 'pre-wrap',
    },
  },
})
